import { Injectable } from '@angular/core';
import { combineQueries } from '@datorama/akita';
import { AclAssetPermission } from '@y42/acl/models';
import { AbstractFeatureQueryEntity, Y42FeatureToAclAssetResourceType } from '@y42/feature';
import { FeatureAclQuery } from '@y42/feature/acl';
import { CanvasElementType, Y42FeatureType } from '@y42/json-schema';
import { assertNonNullable } from '@y42/util/assert';
import { isNotNullish } from '@y42/util/nullish';
import { combineLatest, debounceTime, filter, map, Observable, shareReplay } from 'rxjs';
import type { VisualizationTabStoreModel, VisualizationTabUIModel } from '../tab/visualization-tab.model';
import { VisualizationTabQuery } from '../tab/visualization-tab.query';
import { VisualizationDashboardState } from './visualization-dashboard-state.model';
import { VisualizationDashboardListViewData, VisualizationDashboardUIModel } from './visualization-dashboard.model';
import { VisualizationDashboardStore } from './visualization-dashboard.store';

@Injectable({ providedIn: 'root' })
export class VisualizationDashboardQuery extends AbstractFeatureQueryEntity<
  VisualizationDashboardState,
  VisualizationDashboardUIModel
> {
  constructor(
    protected readonly store: VisualizationDashboardStore,
    private readonly featureAclQuery: FeatureAclQuery,
    private readonly visualizationTabQuery: VisualizationTabQuery,
  ) {
    super({
      store,
      featureType: Y42FeatureType.VisualizationDashboard,
      toUIModel: (storeModel) => {
        const resourceType = Y42FeatureToAclAssetResourceType[Y42FeatureType.VisualizationDashboard];
        assertNonNullable(resourceType);

        const dashboardHasReadPermission = !!this.featureAclQuery.getPermission({
          resourceType,
          assetPath: storeModel.$path,
          permission: AclAssetPermission.Read,
        })?.allowed;

        const tabs = storeModel.settings.order
          .map(($path) => this.visualizationTabQuery.getUIEntity($path as VisualizationTabStoreModel['$path']))
          .filter(isNotNullish)
          .map((tab) => ({
            ...tab,
            hasReadPermission: dashboardHasReadPermission,
          }));

        return {
          ...storeModel,
          featureType: Y42FeatureType.VisualizationDashboard,
          last_used: this.getValue().last_used?.[storeModel.$path] || null,
          starred: !!this.getValue().starred[storeModel.$path],
          tabs,
          children: tabs,
          hasReadPermission: dashboardHasReadPermission,
        };
      },
    });
  }
  readonly items$: Observable<VisualizationDashboardUIModel[]> = combineQueries([
    this.selectAll(),
    this.select((st) => st.last_used),
    this.select((st) => st.starred),
    this.visualizationTabQuery.selectAll(),
    // TODO
    // this.aclQuery.permissions$,
  ]).pipe(
    debounceTime(0),
    map(([visualizations]) => visualizations.map((visualization) => this.toUIModel(visualization))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly listItems$: Observable<VisualizationDashboardListViewData[]> = combineLatest([
    this.loading$,
    this.items$,
  ]).pipe(
    filter(([loading]) => !loading),
    map(([_, visualizations]) => visualizations.map(this.toListViewModel)),
    map((arr) => arr.flat()),
  );

  readonly active$ = combineQueries([
    this.selectActive(),
    this.select((st) => st.last_used),
    this.select((st) => st.starred),
    this.visualizationTabQuery.selectAll(),
  ]).pipe(
    map(([visualization]) => (visualization ? this.toUIModel(visualization) : undefined)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly activeTabs$: Observable<VisualizationTabUIModel[]> = this.active$.pipe(
    map((active) => (active ? active.tabs : [])),
  );

  readonly usedThemes$ = this.items$.pipe(map((items) => new Set(items.map((item) => item.settings.theme))));

  getActiveDashboardTabs(): VisualizationTabUIModel[] {
    const active = this.getActiveUIModel();

    if (!active) {
      console.warn(`[Visualization]: no active dashboard`);
      return [];
    }

    return active.settings.order
      .map(($path) => this.visualizationTabQuery.getUIEntity($path as VisualizationTabStoreModel['$path']))
      .filter(isNotNullish);
  }

  readonly toListViewModel = (visualization: VisualizationDashboardUIModel): VisualizationDashboardListViewData[] => {
    const tabs = visualization.tabs.map((tab) => {
      const numberOfWidgetInTab = (tab.canvasElements ?? []).filter(
        (canvasElement) => canvasElement.type === CanvasElementType.Widget,
      ).length;
      return {
        tabId: tab.$path,
        tabName: tab.name,
        numberOfWidgets: numberOfWidgetInTab,
        description: tab.description,
      };
    });

    const listItem: VisualizationDashboardListViewData = {
      ...visualization,
      id: visualization.$path,
      child_$path: '' as VisualizationTabStoreModel['$path'],
      child_name: '',
      child_description: '',
      numberOfWidgets: tabs.reduce((sum, { numberOfWidgets }) => sum + numberOfWidgets, 0),
    };

    if (!tabs.length) {
      return [{ ...listItem }];
    }

    return tabs.map((tab) => {
      const tabName = tab.tabName;
      const numberOfWidgets = tab.numberOfWidgets;
      const row: VisualizationDashboardListViewData = {
        ...listItem,
        numberOfWidgets,
        child_$path: tab.tabId,
        child_name: tabName,
        child_description: tab.description,
      };

      return row;
    });
  };
}
